import { useEffect, useState } from "react";
import { Sparkles, ChevronLeft, ChevronRight, Check, Loader2, ExternalLink } from "lucide-react";
import { PropertyThumb } from "@/components/PropertyThumb";
import { ClientAvatar } from "@/components/ClientAvatar";
import { BoundaryLinks } from "@/components/BoundaryReference";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CYCLE_MS = 8000;
const PER_PAGE = 3;

function dueWord(r) {
  if (r.overdue) return "overdue";
  return r.due_today ? "due today" : "due tomorrow";
}

function message(r) {
  if (r.kind === "loaded") {
    const n = r.media_count;
    return `${r.type} is ${dueWord(r)}, but Aryeo already has ${n} ${r.media_label}${n === 1 ? "" : "s"} loaded for this property. It may already be done.`;
  }
  if (r.kind === "auto") return r.message;
  if (r.kind === "start") {
    const who = r.editor_name ? `Make sure ${r.editor_name} has all the files` : "No editor assigned yet, assign one and send the files";
    return `${r.type} is ${dueWord(r)} and still marked New. ${who}, then start editing.`;
  }
  return `${r.type} is due tomorrow and no editor is assigned yet. Assign one and send the files today.`;
}

const ReminderCard = ({ r, onDone, onOpen }) => {
  const [busy, setBusy] = useState(false);
  const loaded = r.kind === "loaded";
  const done = async (e) => {
    e.stopPropagation();
    setBusy(true);
    try { await onDone(r); } finally { setBusy(false); }
  };
  return (
    <div onClick={() => onOpen(r)} data-testid="smart-reminder-item" data-kind={r.kind} data-key={r.key}
      className={`flex min-w-0 cursor-pointer flex-col gap-3 rounded-lg border p-3 transition-colors animate-in fade-in slide-in-from-right-2 duration-300 hover:bg-card/60 ${loaded ? "border-emerald-500/40 bg-emerald-500/10" : "border-amber-500/40 bg-amber-500/10"}`}>
      <div className="flex min-w-0 items-start gap-3">
        <PropertyThumb orderId={r.order_id} show={r.has_thumb} className="h-10 w-14 shrink-0" testId="smart-reminder-thumb" />
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium" data-testid="smart-reminder-address">{r.address}</div>
          {r.client_name && (
            <div className="flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground" data-testid="smart-reminder-client">
              <ClientAvatar name={r.client_name} email={r.client_email} className="h-4 w-4 text-[7px]" /><span className="truncate">{r.client_name}</span>
            </div>
          )}
        </div>
      </div>
      <div className="flex-1 text-xs leading-relaxed text-muted-foreground" data-testid="smart-reminder-message">{message(r)}</div>
      <div className="flex flex-wrap items-center gap-2">
        {r.boundary?.outline && (
          <a href={`${BACKEND_URL}/api/orders/${r.order_id}/parcel-outline.jpg`} target="_blank" rel="noreferrer"
            onClick={(e) => e.stopPropagation()} title="Parcel outline" data-testid="smart-reminder-outline"
            className="shrink-0 overflow-hidden rounded-md border border-amber-500/50">
            <img src={`${BACKEND_URL}/api/orders/${r.order_id}/parcel-outline.jpg`} alt="Parcel outline" className="h-8 w-12 object-cover" />
          </a>
        )}
        {r.boundary && (
          <div className="shrink-0" data-testid="smart-reminder-boundary" onClick={(e) => e.stopPropagation()}>
            <BoundaryLinks links={r.boundary.links.filter((l) => l.id === "onx")} prefix="smart-reminder-boundary" />
          </div>
        )}
        {r.aryeo_link && (
          <a href={r.aryeo_link} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
            data-testid="smart-reminder-aryeo-link" title="View in Aryeo"
            className="flex h-8 shrink-0 items-center gap-1.5 rounded-full border border-border bg-card px-3 text-xs font-medium text-primary transition-colors hover:bg-secondary">
            <ExternalLink className="h-3.5 w-3.5" />Aryeo
          </a>
        )}
        <button onClick={done} disabled={busy} title={loaded ? "Mark product done" : r.kind === "start" ? "Set status to Editing" : "Got it, hide this reminder"}
          data-testid="smart-reminder-done"
          className="ml-auto flex h-8 shrink-0 items-center gap-1.5 rounded-full border border-border bg-card px-3 text-xs font-medium transition-colors hover:border-emerald-500/60 hover:text-emerald-400">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
          {loaded ? "Mark done" : r.kind === "start" ? "Start editing" : "Got it"}
        </button>
      </div>
    </div>
  );
};

const NavButton = ({ dir, onClick, testid, label }) => (
  <button onClick={onClick} aria-label={label} data-testid={testid}
    className="flex h-10 w-10 items-center justify-center rounded-full border border-border bg-card text-foreground shadow-sm transition-colors hover:border-primary hover:bg-primary hover:text-primary-foreground">
    {dir < 0 ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
  </button>
);

export const SmartReminders = ({ reminders, onDone, onOpen }) => {
  const [page, setPage] = useState(0);
  const [paused, setPaused] = useState(false);
  const count = reminders.length;
  const pages = Math.ceil(count / PER_PAGE);

  useEffect(() => { if (page >= pages) setPage(0); }, [pages, page]);
  useEffect(() => {
    if (paused || pages < 2) return undefined;
    const t = setInterval(() => { setPage((p) => (p + 1) % pages); }, CYCLE_MS);
    return () => { clearInterval(t); };
  }, [paused, pages]);

  if (!count) return null;
  const p = Math.min(page, pages - 1);
  const shown = reminders.slice(p * PER_PAGE, p * PER_PAGE + PER_PAGE);
  const step = (d) => (e) => { e.stopPropagation(); setPage((x) => (x + d + pages) % pages); };

  return (
    <div data-testid="smart-reminders" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}
      className="rounded-xl border border-border bg-card/40 p-3">
      <div className="mb-3 flex items-center gap-2 px-1 text-xs">
        <Sparkles className="h-3.5 w-3.5 text-primary" />
        <span className="overline text-muted-foreground">Smart reminders</span>
        <span className="font-mono text-muted-foreground" data-testid="smart-reminders-counter">
          {p * PER_PAGE + 1}–{p * PER_PAGE + shown.length} of {count}
        </span>
        {pages > 1 && (
          <div className="ml-auto flex items-center gap-2">
            <NavButton dir={-1} onClick={step(-1)} testid="smart-reminders-prev" label="Previous reminders" />
            <NavButton dir={1} onClick={step(1)} testid="smart-reminders-next" label="Next reminders" />
          </div>
        )}
      </div>
      <div key={p} className="grid grid-cols-1 gap-3 md:grid-cols-3">
        {shown.map((r) => <ReminderCard key={r.key} r={r} onDone={onDone} onOpen={onOpen} />)}
      </div>
    </div>
  );
};
